'use client';

import { Avatar, Tag, type TagProps } from '@lulalife/design-system';
import { DSSurface } from './DSSurface';

export type ContactCardBadge = {
  color?: TagProps['color'];
  label: string;
  title?: string;
};

type ContactCardProps = {
  image?: string | null;
  initials: string;
  name: string;
  label: string;
  phone?: string | null;
  phoneDisplay?: string | null;
  email?: string | null;
  badges?: ContactCardBadge[];
  'data-testid'?: string;
};

const LINK = 'block w-fit break-all desktop-body-sm text-text-link no-underline';
const NONE = 'block desktop-body-sm text-text-secondary';

/**
 * Local copy of the Figma library's `Contact Card`, which npm 0.30.28 doesn't ship. Swap this one
 * component when the DS publishes it. Medium avatar aligned to the top, then name, role, phone,
 * email and badges.
 */
export const ContactCard = ({
  image,
  initials,
  name,
  label,
  phone,
  phoneDisplay,
  email,
  badges = [],
  'data-testid': testId,
}: ContactCardProps) => {
  const part = (suffix: string) => (testId ? `${testId}-${suffix}` : undefined);
  return (
    <DSSurface
      className="flex items-start gap-space-12 rounded-card-radius border border-card-border bg-card-bg p-space-16 font-sans"
      data-testid={testId}
    >
      <span className="shrink-0" data-testid={part('avatar')}>
        {image ? (
          <Avatar type="photo" size="md" src={image} alt={name} />
        ) : (
          <Avatar type="initial" size="md" initials={initials} />
        )}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-space-2">
        <div
          className="desktop-body-md-base break-words text-text-primary"
          data-testid={part('name')}
        >
          {name}
        </div>
        <div className="desktop-body-sm text-text-secondary" data-testid={part('role')}>
          {label}
        </div>
        {phone !== undefined ? (
          phone ? (
            <a href={`tel:${phone}`} className={LINK} data-testid={part('phone')}>
              {phoneDisplay ?? phone}
            </a>
          ) : (
            <span className={NONE} data-testid={part('phone')}>
              —
            </span>
          )
        ) : null}
        {email !== undefined ? (
          email ? (
            <a href={`mailto:${email}`} className={LINK} data-testid={part('email')}>
              {email}
            </a>
          ) : (
            <span className={NONE} data-testid={part('email')}>
              —
            </span>
          )
        ) : null}
        {badges.length > 0 ? (
          <div className="flex flex-wrap gap-space-4 pt-space-4">
            {badges.map((badge) => (
              <Tag
                key={badge.label}
                size="compact"
                color={badge.color ?? 'neutral'}
                label={badge.label}
                infoTooltipContent={badge.title}
              />
            ))}
          </div>
        ) : null}
      </div>
    </DSSurface>
  );
};
